import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { ErrorOption, SubmitHandler, useForm } from 'react-hook-form';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import has from 'lodash/has';
import omit from 'lodash/omit';
import { Button } from 'ui';
import { useConfirm } from 'ui/contexts/overlay/Confirm';
import useToastContext from 'ui/hooks/useToast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { OverlayConfig, OverlayProps } from 'ui/models/overlay';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { updateCacheById } from 'graphql/apollo/cache';
import {
  convertToDecimal,
  dateWithoutTimezone,
  dateWithTimezone,
  setFormError,
} from 'utils/form';
import PisCofinsTaxFields, {
  FormFields,
  FORM_FIELDS,
} from 'pisCofinsTaxes/components/form/PisCofinsTaxFields';
import DeleteButton from 'pisCofinsTaxes/components/DeleteButton';
import {
  PowerDistributionUnitPisCofinsTaxUpdated,
  PowerDistributionUnitPisCofinsTaxUpdateMutationVariables,
  POWER_DISTRIBUTION_UNIT_PIS_COFINS_UPDATE_MUTATION,
} from 'pisCofinsTaxes/graphql/pisCofinsTaxUpdateMutation';
import {
  PowerDistributionUnitPisCofinsTaxTypename,
  POWER_DISTRIBUTION_UNIT_PIS_COFINS_TAX_QUERY,
} from 'pisCofinsTaxes/graphql/pisCofinsTaxQuery';
import { DEFAULT_OVERLAY_CONFIG, ToastProps } from 'ui/contexts/overlay/Toast';
import {
  PisCofinsTaxDeleteMutationVariables,
  PIS_COFINS_TAX_DELETE_MUTATION,
} from 'pisCofinsTaxes/graphql/pisCofinsTaxesDeleteMutation';
import { unMaskFormFields } from 'utils/unmask';

const DASHBOARD_PIS_COFINS_TAXES_ROUTE = '/dashboard/taxes-fees/pis-cofins-taxes';

const BREADCRUMB_PAGES: Page[] = [
  {
    name: 'PIS/COFINS',
    route: DASHBOARD_PIS_COFINS_TAXES_ROUTE,
    current: false,
  },
  {
    name: 'Edição de PIS/COFINS',
    route: null,
    current: true,
  },
];

const TITLE = 'Edição de PIS/COFINS';

const UPDATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar atualizar o PIS/COFINS',
  title: 'Algo deu errado!',
  variant: 'danger',
};
const UPDATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao atualizar o PIS/COFINS.',
};
const FETCH_ERROR_TOAST: ToastProps = {
  title: 'Algo deu errado!',
  variant: 'danger',
  text: 'Não foi possível carregar o PIS/COFINS',
};

export default function EditPisCofinsTaxesPage() {
  const { ConfirmOverlay, showConfirm, closeConfirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { t } = useTranslation();

  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [pisCofinsTaxUpdateMutation, { loading: updateLoading }] = useMutation<
    PowerDistributionUnitPisCofinsTaxUpdated,
    PowerDistributionUnitPisCofinsTaxUpdateMutationVariables
  >(POWER_DISTRIBUTION_UNIT_PIS_COFINS_UPDATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(UPDATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(UPDATE_SUCCESS_TOAST);
      push(DASHBOARD_PIS_COFINS_TAXES_ROUTE);
    },
  });

  const [pisCofinsTaxDeleteMutation, { loading: deleteLoading }] =
    useMutation<PisCofinsTaxDeleteMutationVariables>(
      PIS_COFINS_TAX_DELETE_MUTATION,
      {
        onError() {
          closeConfirm();
          setConfirmOverlayProps(DEFAULT_OVERLAY_CONFIG);
          addToast(UPDATE_ERROR_TOAST);
        },
        onCompleted() {
          closeConfirm();
          setConfirmOverlayProps(DEFAULT_OVERLAY_CONFIG);
          addToast(UPDATE_SUCCESS_TOAST);
          push(DASHBOARD_PIS_COFINS_TAXES_ROUTE);
        },
      }
    );

  const [
    pisCofinsTaxQuery,
    { data, loading: fetchPixCofinsTaxLoading, error: fetchPisCofinsTaxError },
  ] = useLazyQuery(POWER_DISTRIBUTION_UNIT_PIS_COFINS_TAX_QUERY, {
    variables: {
      id: id,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    setValue,
    control,
  } = useForm<FormFields>();

  useEffect(() => {
    if (fetchPisCofinsTaxError) {
      addToast(FETCH_ERROR_TOAST);
    }
  }, [addToast, fetchPisCofinsTaxError]);

  useEffect(() => {
    if (data?.powerDistributionUnitPisCofinsTax) {
      const { pisCofinsValidityStartAt, pisCofinsValidityEndAt } =
        data.powerDistributionUnitPisCofinsTax;
      const pisCofinsFields = Object.assign(
        omit(
          data.powerDistributionUnitPisCofinsTax,
          'pisCofinsValidityStartDate',
          'pisCofinsValidityEndDate'
        ) as FormFields,
        {
          pisCofinsValidity: [
            dateWithTimezone(new Date(pisCofinsValidityStartAt)),
            dateWithTimezone(new Date(pisCofinsValidityEndAt)),
          ],
        }
      );

      FORM_FIELDS.forEach((field) => setValue(field, pisCofinsFields[field]));

      setValue(
        'pisCofinsPisFee',
        convertToDecimal(data.powerDistributionUnitPisCofinsTax.pisCofinsPisFee)
      );

      setValue(
        'pisCofinsCofinsFee',
        convertToDecimal(data.powerDistributionUnitPisCofinsTax.pisCofinsCofinsFee)
      );
    }
  }, [data, setValue]);

  useEffect(() => {
    pisCofinsTaxQuery();
  }, [pisCofinsTaxQuery, id]);

  const [confirmOverlayProps, setConfirmOverlayProps] = useState<OverlayProps>(
    DEFAULT_OVERLAY_CONFIG
  );

  const isLoading = !!(deleteLoading || updateLoading || fetchPixCofinsTaxLoading);

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  const onSubmit: SubmitHandler<FormFields> = (
    powerDistributionUnitPisCofinsTaxUpdateInput
  ) => {
    const [validityStart, validityEnd] =
      powerDistributionUnitPisCofinsTaxUpdateInput.pisCofinsValidity;

    powerDistributionUnitPisCofinsTaxUpdateInput = unMaskFormFields(
      powerDistributionUnitPisCofinsTaxUpdateInput
    );

    pisCofinsTaxUpdateMutation({
      variables: {
        powerDistributionUnitPisCofinsTaxUpdateInput: {
          ...omit(
            powerDistributionUnitPisCofinsTaxUpdateInput,
            'powerDistributionUnitId',
            'pisCofinsValidity'
          ),
          id: Number(id),
          pisCofinsValidityStartAt: dateWithoutTimezone(validityStart)
            .toISOString()
            .split('T')[0],
          pisCofinsValidityEndAt: dateWithoutTimezone(validityEnd)
            .toISOString()
            .split('T')[0],
        },
      },
    });
  };

  const onConfirmDelete = () =>
    pisCofinsTaxDeleteMutation({
      variables: { id: id },
      update(cache) {
        updateCacheById(
          cache,
          Number(id),
          PowerDistributionUnitPisCofinsTaxTypename
        );
      },
    });

  const onClickDelete = (overlayConfig: OverlayConfig) => {
    setConfirmOverlayProps({
      ...overlayConfig,
      onConfirm: onConfirmDelete,
    });

    showConfirm();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<></>}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm title={TITLE} breadcrumbPages={BREADCRUMB_PAGES}>
            {data?.powerDistributionUnitPisCofinsTax && (
              <>
                {' '}
                <DeleteButton onClick={onClickDelete} disabled={isLoading} />
                <Button type="submit" size="sm" disabled={isLoading}>
                  Salvar
                </Button>
              </>
            )}
            <ConfirmOverlay {...confirmOverlayProps} onCancel={closeConfirm} />
          </DashboardMainHeaderForm>
        }
      >
        {data?.powerDistributionUnitPisCofinsTax && (
          <PisCofinsTaxFields
            errors={errors}
            register={register}
            control={control}
            powerDistributionUnits={[
              {
                id: data.powerDistributionUnitPisCofinsTax.powerDistributionUnitId,
                powerDistributionUnitLegalName:
                  data.powerDistributionUnitPisCofinsTax.powerDistributionUnit
                    .powerDistributionUnitLegalName,
              },
            ]}
            powerDistributionUnitValue={
              data.powerDistributionUnitPisCofinsTax.powerDistributionUnitId
            }
            disablePowerDistributionUnitSelect
          />
        )}
      </Dashboard>
    </form>
  );
}
